import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Paper
} from '@mui/material';
import {
  Bolt as BoltIcon,
  Palette as PaletteIcon,
  Article as ArticleIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';

const HowItWorks = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  const steps = [
    {
      id: 1,
      number: "01",
      icon: <BoltIcon sx={{ fontSize: 40 }} />,
      emoji: "🚀",
      title: "Hızlı Kurulum",
      subtitle: "15 dakikada platformunuz hazır",
      description: "Profesyonel haber siteniz 15 dakikada kullanıma hazır. Karmaşık kurulum yok, sadece basit adımlar.",
      color: "#1e40af",
      gradient: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
      features: ["Otomatik kurulum", "Hazır şablonlar", "Demo içerik"]
    },
    {
      id: 2,
      number: "02",
      icon: <PaletteIcon sx={{ fontSize: 40 }} />,
      emoji: "🎨",
      title: "Sayfa Tasarlayın",
      subtitle: "Görerek tasarlama ile sayfalarınızı tasarlayın",
      description: "70+ widget ile görerek tasarlama ile sayfalarınızı tasarlayın. Kod bilgisi gerektirmez.",
      color: "#dc2626",
      gradient: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
      features: ["70+ widget seçin", "Yerleştirin", "Canlı önizleyin"]
    },
    {
      id: 3,
      number: "03",
      icon: <ArticleIcon sx={{ fontSize: 40 }} />,
      emoji: "📰",
      title: "Haber Yayınlayın",
      subtitle: "Hemen içerik üretmeye başlayın",
      description: "Gelişmiş editör ile haber ekleyin, editör onayından geçirin ve anında yayına alın.",
      color: "#059669",
      gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
      features: ["Haber ekleyin", "Editör onayı", "Anında yayında"]
    }
  ];

  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.3 }
    );

    const currentRef = sectionRef.current;
    
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // Auto step animation
  useEffect(() => {
    if (!isInView) return;
    
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isInView, steps.length]);

  return (
   <Box sx={{ backgroundColor:'#f1f6fb' }}>
     <Box
      ref={sectionRef}
      sx={{
        py: { xs: 10, md: 16 },
        background: 'linear-gradient(44deg, #0f172a 0%, #1e3a8a 100%)',
        borderRadius: {xs : 0, md : '400px 0 0 0'},
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 30% 30%, rgba(59, 130, 246, 0.15) 0%, transparent 60%),
            radial-gradient(circle at 70% 70%, rgba(16, 185, 129, 0.1) 0%, transparent 60%)
          `,
          pointerEvents: 'none'
        }
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Header */}
        <Box textAlign="center" mb={10}>
          <Typography
            variant="overline"
            sx={{
              fontSize: '0.9rem',
              fontWeight: 900,
              letterSpacing: '0.1em',
              color: '#3b82f6',
              mb: 2,
              display: 'block',
              textTransform: 'uppercase',
              animation: isInView ? 'fadeInUp 0.6s ease-out' : 'none'
            }}
          >
            NASIL ÇALIŞIR
          </Typography>
          
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.8rem', md: '4rem' },
              fontWeight: 800,
              mb: 4,
              color: 'white',
              lineHeight: 1.1,
              animation: isInView ? 'fadeInUp 0.8s ease-out 0.2s both' : 'none'
            }}
          >
            Sadece 3 Adımda Başlayın
          </Typography>
          
          {/* <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 400,
              animation: isInView ? 'fadeInUp 1s ease-out 0.4s both' : 'none'
            }}
          >
            Profesyonel haber siteniz 15 dakikada hazır, kod bilgisi gerektirmez
          </Typography> */}
        </Box>

        {/* Steps Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 4,
            position: 'relative',
            mb: 8
          }}
        >
          {steps.map((step, index) => (
            <Paper
              key={step.id}
              elevation={0}
              onMouseEnter={() => setActiveStep(index)}
              sx={{
                background: '#ffffff',
                border: activeStep === index
                  ? `2px solid ${step.color}`
                  : '1px solid #e2e8f0',
                borderRadius: 4,
                p: 5,
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: activeStep === index ? 'translateY(-12px)' : 'translateY(0)',
                boxShadow: activeStep === index 
                  ? `0 25px 50px ${step.color}20` 
                  : '0 4px 20px rgba(0, 0, 0, 0.05)',
                animation: isInView ? `slideInUp 0.8s ease-out ${index * 0.2}s both` : 'none',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: step.gradient,
                  transform: activeStep === index ? 'scaleX(1)' : 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.5s ease'
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 100,
                  height: 100,
                  background: `radial-gradient(circle, ${step.color}15 0%, transparent 70%)`,
                  borderRadius: '50%',
                  opacity: activeStep === index ? 1 : 0,
                  transition: 'opacity 0.4s ease'
                }
              }}
            >
              {/* Step Number Badge */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: activeStep === index 
                    ? step.gradient
                    : `${step.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: activeStep === index ? 'white' : step.color,
                  fontSize: '1.1rem',
                  fontWeight: 800,
                  transition: 'all 0.5s ease'
                }}
              >
                {step.number}
              </Box>

              {/* Icon with Emoji */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  background: activeStep === index
                    ? step.gradient
                    : `${step.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: activeStep === index ? 'white' : step.color,
                  mb: 3,
                  transition: 'all 0.5s ease',
                  position: 'relative',
                  fontSize: '2.5rem'
                }}
              >
                {activeStep === index ? step.icon : step.emoji}
              </Box>

              {/* Content */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 1,
                  fontSize: '1.5rem'
                }}
              >
                {step.title}
              </Typography>
              
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  background: step.gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 3,
                  fontSize: '1.1rem'
                }}
              >
                {step.subtitle}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#64748b',
                  lineHeight: 1.7,
                  mb: 3
                }}
              >
                {step.description}
              </Typography>

              {/* Features List */}
              <Stack spacing={2}>
                {step.features.map((feature, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      opacity: activeStep === index ? 1 : 0.7,
                      transform: activeStep === index 
                        ? 'translateX(0)' 
                        : 'translateX(-10px)',
                      transition: `all 0.4s ease ${idx * 0.1}s`
                    }}
                  >
                    <CheckIcon 
                      sx={{ 
                        fontSize: 20, 
                        color: step.color,
                        flexShrink: 0
                      }} 
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#475569',
                        fontWeight: 500
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          ))}
        </Box>
        <Box
        sx={{
          mt: {xs : '0px', md : '300px'},
          py: { xs: 10, md: 16 },
          background: '#0f172a',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.1rem', md: '3.5rem' },
              fontWeight: 800,
              mb: 3
            }}
          >
            Haber Yönetimini Profesyonelleştirin
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 6,
              opacity: 0.9,
              lineHeight: 1.6
            }}
          >
            Güçlü içerik yönetim sistemi ile haber sitenizi bir üst seviyeye taşıyın
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              color: 'white',
              px: 6,
              py: 2.5,
              fontSize: '1.2rem',
              fontWeight: 700,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 30px rgba(59, 130, 246, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
           İletişime Geçin
          </Button>
        </Container>
      </Box>
      </Container>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </Box>
   </Box>
  );
};

export default HowItWorks;


// import React, { useState, useEffect, useRef } from 'react';
// import {
//   Box,
//   Container,
//   Typography,
//   Button,
//   Stack,
//   Paper,
//   Grid,
//   Avatar,
//   Chip
// } from '@mui/material';
// import {
//   Bolt as BoltIcon,
//   Palette as PaletteIcon,
//   Article as ArticleIcon,
//   CheckCircle as CheckIcon,
//   ArrowForward as ArrowIcon,
//   PlayCircle as PlayIcon,
//   AutoAwesome as MagicIcon
// } from '@mui/icons-material';

// const HowItWorks = () => {
//   const [activeStep, setActiveStep] = useState(0);
//   const [isInView, setIsInView] = useState(false);
//   const [progress, setProgress] = useState(0);
//   const sectionRef = useRef(null);

//   const steps = [
//     {
//       id: 1,
//       number: "01",
//       icon: <BoltIcon sx={{ fontSize: 40 }} />,
//       emoji: "🚀",
//       title: "Hızlı Kurulum",
//       subtitle: "15 dakikada platformunuz hazır",
//       description: "Profesyonel haber siteniz 15 dakikada kullanıma hazır. Karmaşık kurulum yok, sadece basit adımlar.",
//       color: "#1e40af",
//       gradient: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
//       features: ["Otomatik kurulum", "Hazır şablonlar", "Demo içerik"],
//       mockup: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop",
//       stats: { time: "15 dk", difficulty: "Kolay", users: "500+" }
//     },
//     {
//       id: 2,
//       number: "02",
//       icon: <PaletteIcon sx={{ fontSize: 40 }} />,
//       emoji: "🎨",
//       title: "Sayfa Tasarlayın",
//       subtitle: "Sürükle-bırak ile özelleştirin",
//       description: "70+ widget ile sürükle-bırak editörde sayfalarınızı tasarlayın. Kod bilgisi gerektirmez.",
//       color: "#dc2626",
//       gradient: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
//       features: ["70+ widget seçin", "Yerleştirin", "Canlı önizleyin"],
//       mockup: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&h=400&fit=crop",
//       stats: { time: "10 dk", difficulty: "Çok Kolay", users: "450+" }
//     },
//     {
//       id: 3,
//       number: "03",
//       icon: <ArticleIcon sx={{ fontSize: 40 }} />,
//       emoji: "📰",
//       title: "Haber Yayınlayın",
//       subtitle: "Hemen içerik üretmeye başlayın",
//       description: "Gelişmiş editör ile haber ekleyin, editör onayından geçirin ve anında yayına alın.",
//       color: "#059669",
//       gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
//       features: ["Haber ekleyin", "Editör onayı", "Anında yayında"],
//       mockup: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600&h=400&fit=crop",
//       stats: { time: "5 dk", difficulty: "Kolay", users: "600+" }
//     }
//   ];

//   // Intersection Observer
//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting) {
//           setIsInView(true);
//         }
//       },
//       { threshold: 0.3 }
//     );

//     const currentRef = sectionRef.current;
    
//     if (currentRef) {
//       observer.observe(currentRef);
//     }

//     return () => {
//       if (currentRef) {
//         observer.unobserve(currentRef);
//       }
//     };
//   }, []);

//   // Auto step animation with progress
//   useEffect(() => {
//     if (!isInView) return;
    
//     // Progress bar animation
//     const progressInterval = setInterval(() => {
//       setProgress((prev) => {
//         if (prev >= 100) {
//           setActiveStep((current) => (current + 1) % steps.length);
//           return 0;
//         }
//         return prev + 1;
//       });
//     }, 40); // 4 seconds total (100 * 40ms)

//     return () => clearInterval(progressInterval);
//   }, [isInView, steps.length, activeStep]);

//   return (
//     <Box sx={{ backgroundColor: '#f1f6fb' }}>
//       <Box
//         ref={sectionRef}
//         sx={{
//           py: { xs: 10, md: 16 },
//           background: 'linear-gradient(44deg, #0f172a 0%, #1e3a8a 100%)',
//           borderRadius: { xs: 0, md: '400px 0 0 0' },
//           position: 'relative',
//           overflow: 'hidden',
//           '&::before': {
//             content: '""',
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             right: 0,
//             bottom: 0,
//             background: `
//               radial-gradient(circle at 30% 30%, rgba(59, 130, 246, 0.15) 0%, transparent 60%),
//               radial-gradient(circle at 70% 70%, rgba(16, 185, 129, 0.1) 0%, transparent 60%)
//             `,
//             pointerEvents: 'none'
//           },
//           // Animated dots pattern
//           '&::after': {
//             content: '""',
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             right: 0,
//             bottom: 0,
//             backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)',
//             backgroundSize: '30px 30px',
//             opacity: 0.5,
//             animation: 'movePattern 20s linear infinite'
//           }
//         }}
//       >
//         <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
//           {/* Header */}
//           <Box textAlign="center" mb={8}>
//             <Chip
//               icon={<MagicIcon />}
//               label="NASIL ÇALIŞIR"
//               sx={{
//                 fontSize: '0.85rem',
//                 fontWeight: 700,
//                 letterSpacing: '0.05em',
//                 background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
//                 color: 'white',
//                 mb: 3,
//                 px: 2,
//                 py: 2.5,
//                 animation: isInView ? 'fadeInUp 0.6s ease-out' : 'none'
//               }}
//             />
            
//             <Typography
//               variant="h2"
//               sx={{
//                 fontSize: { xs: '2.8rem', md: '4rem' },
//                 fontWeight: 800,
//                 mb: 3,
//                 color: 'white',
//                 lineHeight: 1.1,
//                 animation: isInView ? 'fadeInUp 0.8s ease-out 0.2s both' : 'none'
//               }}
//             >
//               Sadece{' '}
//               <Box
//                 component="span"
//                 sx={{
//                   background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
//                   backgroundClip: 'text',
//                   WebkitBackgroundClip: 'text',
//                   WebkitTextFillColor: 'transparent'
//                 }}
//               >
//                 3 Adımda
//               </Box>
//               {' '}Başlayın
//             </Typography>
            
//             <Typography
//               variant="h6"
//               sx={{
//                 color: 'rgba(255, 255, 255, 0.7)',
//                 maxWidth: '600px',
//                 mx: 'auto',
//                 lineHeight: 1.6,
//                 fontWeight: 400,
//                 animation: isInView ? 'fadeInUp 1s ease-out 0.4s both' : 'none'
//               }}
//             >
//               Profesyonel haber siteniz 15 dakikada hazır, kod bilgisi gerektirmez
//             </Typography>
//           </Box>

//           {/* Main Content - Split Layout */}
//           <Grid container spacing={6} alignItems="center">
//             {/* Left Side - Steps Navigation */}
//             <Grid item xs={12} md={5}>
//               <Stack spacing={3}>
//                 {steps.map((step, index) => (
//                   <Paper
//                     key={step.id}
//                     onClick={() => {
//                       setActiveStep(index);
//                       setProgress(0);
//                     }}
//                     sx={{
//                       background: activeStep === index
//                         ? 'rgba(255, 255, 255, 0.95)'
//                         : 'rgba(255, 255, 255, 0.05)',
//                       backdropFilter: 'blur(10px)',
//                       border: activeStep === index
//                         ? `2px solid ${step.color}`
//                         : '1px solid rgba(255, 255, 255, 0.1)',
//                       borderRadius: 3,
//                       p: 3,
//                       cursor: 'pointer',
//                       position: 'relative',
//                       overflow: 'hidden',
//                       transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
//                       transform: activeStep === index ? 'translateX(10px)' : 'translateX(0)',
//                       animation: isInView ? `slideInLeft 0.8s ease-out ${index * 0.15}s both` : 'none',
//                       '&:hover': {
//                         background: activeStep === index
//                           ? 'rgba(255, 255, 255, 0.95)'
//                           : 'rgba(255, 255, 255, 0.1)',
//                         transform: 'translateX(10px)'
//                       },
//                       // Progress bar
//                       '&::before': {
//                         content: '""',
//                         position: 'absolute',
//                         bottom: 0,
//                         left: 0,
//                         height: 3,
//                         width: activeStep === index ? `${progress}%` : '0%',
//                         background: step.gradient,
//                         transition: 'width 0.04s linear'
//                       }
//                     }}
//                   >
//                     <Stack direction="row" spacing={2} alignItems="flex-start">
//                       {/* Step Number */}
//                       <Box
//                         sx={{
//                           width: 60,
//                           height: 60,
//                           borderRadius: 2,
//                           background: activeStep === index
//                             ? step.gradient
//                             : `${step.color}20`,
//                           display: 'flex',
//                           alignItems: 'center',
//                           justifyContent: 'center',
//                           flexShrink: 0,
//                           transition: 'all 0.4s ease'
//                         }}
//                       >
//                         <Typography
//                           sx={{
//                             fontSize: '1.5rem',
//                             fontWeight: 800,
//                             color: activeStep === index ? 'white' : step.color
//                           }}
//                         >
//                           {step.number}
//                         </Typography>
//                       </Box>

//                       {/* Content */}
//                       <Box flex={1}>
//                         <Typography
//                           variant="h6"
//                           sx={{
//                             fontWeight: 700,
//                             color: activeStep === index ? '#1e293b' : 'white',
//                             mb: 0.5,
//                             fontSize: '1.2rem'
//                           }}
//                         >
//                           {step.title}
//                         </Typography>
                        
//                         <Typography
//                           variant="body2"
//                           sx={{
//                             color: activeStep === index
//                               ? '#64748b'
//                               : 'rgba(255, 255, 255, 0.6)',
//                             mb: 2,
//                             lineHeight: 1.5
//                           }}
//                         >
//                           {step.description}
//                         </Typography>

//                         {/* Stats */}
//                         {activeStep === index && (
//                           <Stack direction="row" spacing={2} flexWrap="wrap">
//                             <Chip
//                               label={`⏱️ ${step.stats.time}`}
//                               size="small"
//                               sx={{
//                                 background: `${step.color}15`,
//                                 color: step.color,
//                                 fontWeight: 600,
//                                 fontSize: '0.75rem'
//                               }}
//                             />
//                             <Chip
//                               label={`✨ ${step.stats.difficulty}`}
//                               size="small"
//                               sx={{
//                                 background: `${step.color}15`,
//                                 color: step.color,
//                                 fontWeight: 600,
//                                 fontSize: '0.75rem'
//                               }}
//                             />
//                             <Chip
//                               label={`👥 ${step.stats.users}`}
//                               size="small"
//                               sx={{
//                                 background: `${step.color}15`,
//                                 color: step.color,
//                                 fontWeight: 600,
//                                 fontSize: '0.75rem'
//                               }}
//                             />
//                           </Stack>
//                         )}
//                       </Box>

//                       {/* Arrow indicator */}
//                       {activeStep === index && (
//                         <ArrowIcon
//                           sx={{
//                             color: step.color,
//                             fontSize: 28,
//                             animation: 'pulse 2s ease-in-out infinite'
//                           }}
//                         />
//                       )}
//                     </Stack>
//                   </Paper>
//                 ))}
//               </Stack>
//             </Grid>

//             {/* Right Side - Visual Preview */}
//             <Grid item xs={12} md={7}>
//               <Box
//                 sx={{
//                   position: 'relative',
//                   animation: isInView ? 'fadeInRight 1s ease-out 0.4s both' : 'none'
//                 }}
//               >
//                 {/* Main Preview Card */}
//                 <Paper
//                   elevation={0}
//                   sx={{
//                     background: 'rgba(255, 255, 255, 0.95)',
//                     backdropFilter: 'blur(20px)',
//                     borderRadius: 4,
//                     p: 4,
//                     position: 'relative',
//                     overflow: 'hidden',
//                     border: `2px solid ${steps[activeStep].color}`,
//                     boxShadow: `0 30px 60px ${steps[activeStep].color}30`
//                   }}
//                 >
//                   {/* Gradient Overlay */}
//                   <Box
//                     sx={{
//                       position: 'absolute',
//                       top: 0,
//                       left: 0,
//                       right: 0,
//                       height: 6,
//                       background: steps[activeStep].gradient
//                     }}
//                   />

//                   {/* Step Icon Badge */}
//                   <Box
//                     sx={{
//                       position: 'absolute',
//                       top: 20,
//                       right: 20,
//                       width: 70,
//                       height: 70,
//                       borderRadius: '50%',
//                       background: steps[activeStep].gradient,
//                       display: 'flex',
//                       alignItems: 'center',
//                       justifyContent: 'center',
//                       color: 'white',
//                       fontSize: '2.5rem',
//                       boxShadow: `0 10px 30px ${steps[activeStep].color}40`,
//                       animation: 'float 3s ease-in-out infinite'
//                     }}
//                   >
//                     {steps[activeStep].emoji}
//                   </Box>

//                   {/* Content */}
//                   <Box mb={3}>
//                     <Typography
//                       variant="h4"
//                       sx={{
//                         fontWeight: 800,
//                         color: '#1e293b',
//                         mb: 1,
//                         fontSize: '2rem'
//                       }}
//                     >
//                       {steps[activeStep].title}
//                     </Typography>
                    
//                     <Typography
//                       variant="h6"
//                       sx={{
//                         fontWeight: 600,
//                         background: steps[activeStep].gradient,
//                         backgroundClip: 'text',
//                         WebkitBackgroundClip: 'text',
//                         WebkitTextFillColor: 'transparent',
//                         mb: 3,
//                         fontSize: '1.2rem'
//                       }}
//                     >
//                       {steps[activeStep].subtitle}
//                     </Typography>

//                     <Typography
//                       variant="body1"
//                       sx={{
//                         color: '#64748b',
//                         lineHeight: 1.7,
//                         mb: 4,
//                         fontSize: '1.05rem'
//                       }}
//                     >
//                       {steps[activeStep].description}
//                     </Typography>

//                     {/* Features List */}
//                     <Stack spacing={2} mb={4}>
//                       {steps[activeStep].features.map((feature, idx) => (
//                         <Box
//                           key={idx}
//                           sx={{
//                             display: 'flex',
//                             alignItems: 'center',
//                             gap: 2,
//                             animation: `slideInRight 0.5s ease-out ${idx * 0.1}s both`
//                           }}
//                         >
//                           <Box
//                             sx={{
//                               width: 32,
//                               height: 32,
//                               borderRadius: '50%',
//                               background: `${steps[activeStep].color}15`,
//                               display: 'flex',
//                               alignItems: 'center',
//                               justifyContent: 'center',
//                               flexShrink: 0
//                             }}
//                           >
//                             <CheckIcon
//                               sx={{
//                                 fontSize: 18,
//                                 color: steps[activeStep].color
//                               }}
//                             />
//                           </Box>
//                           <Typography
//                             variant="body1"
//                             sx={{
//                               color: '#475569',
//                               fontWeight: 600,
//                               fontSize: '1rem'
//                             }}
//                           >
//                             {feature}
//                           </Typography>
//                         </Box>
//                       ))}
//                     </Stack>
//                   </Box>

//                   {/* Mockup Image */}
//                   <Box
//                     sx={{
//                       borderRadius: 3,
//                       overflow: 'hidden',
//                       position: 'relative',
//                       height: 300,
//                       background: `linear-gradient(135deg, ${steps[activeStep].color}20 0%, ${steps[activeStep].color}10 100%)`,
//                       display: 'flex',
//                       alignItems: 'center',
//                       justifyContent: 'center'
//                     }}
//                   >
//                     <Box
//                       component="img"
//                       src={steps[activeStep].mockup}
//                       alt={steps[activeStep].title}
//                       sx={{
//                         width: '100%',
//                         height: '100%',
//                         objectFit: 'cover',
//                         opacity: 0.9,
//                         transition: 'all 0.5s ease'
//                       }}
//                     />
                    
//                     {/* Play button overlay */}
//                     <Box
//                       sx={{
//                         position: 'absolute',
//                         top: '50%',
//                         left: '50%',
//                         transform: 'translate(-50%, -50%)',
//                         width: 80,
//                         height: 80,
//                         borderRadius: '50%',
//                         background: 'rgba(255, 255, 255, 0.95)',
//                         display: 'flex',
//                         alignItems: 'center',
//                         justifyContent: 'center',
//                         cursor: 'pointer',
//                         transition: 'all 0.3s ease',
//                         '&:hover': {
//                           transform: 'translate(-50%, -50%) scale(1.1)',
//                           boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
//                         }
//                       }}
//                     >
//                       <PlayIcon
//                         sx={{
//                           fontSize: 40,
//                           color: steps[activeStep].color
//                         }}
//                       />
//                     </Box>
//                   </Box>
//                 </Paper>

//                 {/* Floating Stats Cards */}
//                 <Paper
//                   elevation={0}
//                   sx={{
//                     position: 'absolute',
//                     bottom: -30,
//                     left: -30,
//                     background: 'rgba(255, 255, 255, 0.95)',
//                     backdropFilter: 'blur(10px)',
//                     borderRadius: 3,
//                     p: 2.5,
//                     boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
//                     border: '1px solid rgba(255, 255, 255, 0.8)',
//                     animation: 'float 4s ease-in-out infinite',
//                     display: { xs: 'none', md: 'block' }
//                   }}
//                 >
//                   <Stack direction="row" spacing={2} alignItems="center">
//                     <Avatar
//                       sx={{
//                         width: 50,
//                         height: 50,
//                         background: steps[activeStep].gradient
//                       }}
//                     >
//                       <BoltIcon />
//                     </Avatar>
//                     <Box>
//                       <Typography
//                         variant="h5"
//                         sx={{
//                           fontWeight: 800,
//                           color: '#1e293b',
//                           lineHeight: 1
//                         }}
//                       >
//                         {steps[activeStep].stats.users}
//                       </Typography>
//                       <Typography
//                         variant="caption"
//                         sx={{
//                           color: '#64748b',
//                           fontWeight: 600
//                         }}
//                       >
//                         Aktif Kullanıcı
//                       </Typography>
//                     </Box>
//                   </Stack>
//                 </Paper>
//               </Box>
//             </Grid>
//           </Grid>

//           {/* Bottom CTA Section */}
//           <Box
//             sx={{
//               mt: { xs: 8, md: 12 },
//               textAlign: 'center'
//             }}
//           >
//             <Button
//               variant="contained"
//               size="large"
//               endIcon={<ArrowIcon />}
//               sx={{
//                 background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
//                 color: 'white',
//                 px: 6,
//                 py: 2.5,
//                 fontSize: '1.1rem',
//                 fontWeight: 700,
//                 borderRadius: 3,
//                 textTransform: 'none',
//                 boxShadow: '0 20px 40px rgba(59, 130, 246, 0.3)',
//                 '&:hover': {
//                   background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
//                   transform: 'translateY(-3px)',
//                   boxShadow: '0 25px 50px rgba(59, 130, 246, 0.4)'
//                 },
//                 transition: 'all 0.3s ease'
//               }}
//             >
//               Ücretsiz Deneyin
//             </Button>
            
//             <Typography
//               variant="body2"
//               sx={{
//                 color: 'rgba(255, 255, 255, 0.6)',
//                 mt: 2,
//                 fontSize: '0.9rem'
//               }}
//             >
//               💳 Kredi kartı gerektirmez • ⚡ 15 dakikada başlayın
//             </Typography>
//           </Box>
//         </Container>

//         {/* CSS Animations */}
//         <style jsx>{`
//           @keyframes fadeInUp {
//             from {
//               opacity: 0;
//               transform: translateY(30px);
//             }
//             to {
//               opacity: 1;
//               transform: translateY(0);
//             }
//           }
          
//           @keyframes slideInLeft {
//             from {
//               opacity: 0;
//               transform: translateX(-40px);
//             }
//             to {
//               opacity: 1;
//               transform: translateX(0);
//             }
//           }
          
//           @keyframes fadeInRight {
//             from {
//               opacity: 0;
//               transform: translateX(40px);
//             }
//             to {
//               opacity: 1;
//               transform: translateX(0);
//             }
//           }
          
//           @keyframes slideInRight {
//             from {
//               opacity: 0;
//               transform: translateX(20px);
//             }
//             to {
//               opacity: 1;
//               transform: translateX(0);
//             }
//           }
          
//           @keyframes float {
//             0%, 100% {
//               transform: translateY(0);
//             }
//             50% {
//               transform: translateY(-10px);
//             }
//           }
          
//           @keyframes pulse {
//             0%, 100% {
//               opacity: 1;
//               transform: scale(1);
//             }
//             50% {
//               opacity: 0.8;
//               transform: scale(0.95);
//             }
//           }
          
//           @keyframes movePattern {
//             0% {
//               background-position: 0 0;
//             }
//             100% {
//               background-position: 30px 30px;
//             }
//           }
//         `}</style>
//       </Box>

//       {/* Final CTA Section */}
//       <Box
//         sx={{
//           mt: { xs: '0px', md: '300px' },
//           py: { xs: 10, md: 16 },
//           background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
//           color: 'white',
//           textAlign: 'center',
//           position: 'relative',
//           overflow: 'hidden',
//           '&::before': {
//             content: '""',
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             right: 0,
//             bottom: 0,
//             background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
//             pointerEvents: 'none'
//           }
//         }}
//       >
//         <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
//           <Box
//             sx={{
//               width: 80,
//               height: 80,
//               borderRadius: '50%',
//               background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               margin: '0 auto 3rem',
//               animation: 'float 3s ease-in-out infinite'
//             }}
//           >
//             <MagicIcon sx={{ fontSize: 40, color: 'white' }} />
//           </Box>

//           <Typography
//             variant="h2"
//             sx={{
//               fontSize: { xs: '2.5rem', md: '3.5rem' },
//               fontWeight: 800,
//               mb: 3,
//               lineHeight: 1.2
//             }}
//           >
//             Haber Yönetimini{' '}
//             <Box
//               component="span"
//               sx={{
//                 background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
//                 backgroundClip: 'text',
//                 WebkitBackgroundClip: 'text',
//                 WebkitTextFillColor: 'transparent'
//               }}
//             >
//               Profesyonelleştirin
//             </Box>
//           </Typography>
          
//           <Typography
//             variant="h6"
//             sx={{
//               mb: 6,
//               opacity: 0.9,
//               lineHeight: 1.6,
//               maxWidth: '600px',
//               mx: 'auto'
//             }}
//           >
//             Güçlü içerik yönetim sistemi ile haber sitenizi bir üst seviyeye taşıyın
//           </Typography>
          
//           <Stack
//             direction={{ xs: 'column', sm: 'row' }}
//             spacing={3}
//             justifyContent="center"
//           >
//             <Button
//               variant="contained"
//               size="large"
//               endIcon={<ArrowIcon />}
//               sx={{
//                 background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
//                 color: 'white',
//                 px: 6,
//                 py: 2.5,
//                 fontSize: '1.2rem',
//                 fontWeight: 700,
//                 borderRadius: 3,
//                 textTransform: 'none',
//                 boxShadow: '0 20px 40px rgba(59, 130, 246, 0.3)',
//                 '&:hover': {
//                   background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
//                   transform: 'translateY(-3px)',
//                   boxShadow: '0 25px 50px rgba(59, 130, 246, 0.4)'
//                 },
//                 transition: 'all 0.3s ease'
//               }}
//             >
//               İletişime Geçin
//             </Button>
            
//             <Button
//               variant="outlined"
//               size="large"
//               startIcon={<PlayIcon />}
//               sx={{
//                 borderColor: 'rgba(255, 255, 255, 0.3)',
//                 color: 'white',
//                 px: 6,
//                 py: 2.5,
//                 fontSize: '1.2rem',
//                 fontWeight: 700,
//                 borderRadius: 3,
//                 textTransform: 'none',
//                 '&:hover': {
//                   borderColor: 'white',
//                   background: 'rgba(255, 255, 255, 0.1)',
//                   transform: 'translateY(-3px)'
//                 },
//                 transition: 'all 0.3s ease'
//               }}
//             >
//               Demo İzle
//             </Button>
//           </Stack>

//           {/* Trust Badges */}
//           <Stack
//             direction="row"
//             spacing={4}
//             justifyContent="center"
//             mt={6}
//             flexWrap="wrap"
//           >
//             <Chip
//               label="✓ 500+ Aktif Site"
//               sx={{
//                 background: 'rgba(255, 255, 255, 0.1)',
//                 color: 'white',
//                 fontWeight: 600,
//                 fontSize: '0.9rem',
//                 py: 2.5,
//                 px: 1
//               }}
//             />
//             <Chip
//               label="✓ 99.9% Uptime"
//               sx={{
//                 background: 'rgba(255, 255, 255, 0.1)',
//                 color: 'white',
//                 fontWeight: 600,
//                 fontSize: '0.9rem',
//                 py: 2.5,
//                 px: 1
//               }}
//             />
//             <Chip
//               label="✓ 7/24 Destek"
//               sx={{
//                 background: 'rgba(255, 255, 255, 0.1)',
//                 color: 'white',
//                 fontWeight: 600,
//                 fontSize: '0.9rem',
//                 py: 2.5,
//                 px: 1
//               }}
//             />
//           </Stack>
//         </Container>
//       </Box>
//     </Box>
//   );
// };

// export default HowItWorks;